/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { SystemImageType } from '@1po/1po-bff-fe-spec/generated/backoffice/promotion/model/SystemImageType';
import { PromotionImage } from '@1po/1po-bff-fe-spec/generated/backoffice/promotion/response/PromotionImagesResponse';
import { TFunction } from 'i18next';
import { DeleteCrossIcon } from 'assets/icons';
import { CreatePromotionStepProps } from 'domains/promotion/Promotion.types';
import { getUserRights, UserRole } from 'domains/user';
import {
  ImageButtonWrapper,
  ImageNumberWrapper,
  ImageWrapper,
  ImageCategory,
  ImageDeleteWrapper,
} from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PublishStep/SystemImages.styled';
import { usePromotionImages } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/hooks';
import { BlackButton, BlueButton, Box, CenterFlex, Flex, Icon, Image, Text } from 'UI';

interface GroupedImages {
  [category: string]: Array<{
    fileName?: string;
    imageUrl?: string;
    imageKey?: string;
  }>;
}

interface SystemImageProps {
  image?: { fileName?: string; imageUrl?: string; imageKey?: string };
}

const getCategoryLabel = (t: TFunction, category: SystemImageType): string => {
  switch (category) {
    case 'AIR_FILTER':
      return t('backoffice.promotion.default_image.air_filter', 'Air filter');
    case 'BRAKE_PADS':
      return t('backoffice.promotion.default_image.brake_pads', 'Brake pads');
    case 'DISTRIBUTION_KIT':
      return t('backoffice.promotion.default_image.distribution_kit', 'Distribution kit');
    case 'DIVERS':
      return t('backoffice.promotion.default_image.divers', 'Divers');
    case 'ENGINE_OIL':
      return t('backoffice.promotion.default_image.engine_oil', 'Engine oil');
    case 'SPARK_PLUGS':
      return t('backoffice.promotion.default_image.spark_plugs', 'Spark plugs');
    case 'TIRES':
      return t('backoffice.promotion.default_image.tires', 'Tires');
    case 'WINDSHIELD_WIPER_BLADES':
      return t('backoffice.promotion.default_image.windshield_wiper_blades', 'Windshield wiper blades');
    default:
      return '';
  }
};

function groupImagesByCategory(images: PromotionImage[] | undefined): GroupedImages {
  const groupedImages: GroupedImages = {};

  groupedImages['ENGINE_OIL'] = [];

  if (images === undefined) {
    return groupedImages;
  }

  images.forEach((image) => {
    if (image.categories && image.categories.length > 0) {
      image.categories.forEach((category) => {
        if (!groupedImages[category]) {
          groupedImages[category] = [];
        }

        groupedImages[category].push({
          fileName: image.fileName,
          imageUrl: image.imageUrl,
          imageKey: image.imageKey,
        });
      });
    }
  });

  if (groupedImages['ENGINE_OIL'].length === 0) {
    delete groupedImages['ENGINE_OIL'];
  }

  return groupedImages;
}

export default function SystemImages({
  promotion,
  setPromotion,
  setIsOpenImagesModal,
}: CreatePromotionStepProps & { setIsOpenImagesModal: (b: boolean) => void }) {
  const { t } = useTranslation();
  const promotionImage = promotion?.image;
  const defaultImageSet = usePromotionImages();
  const images = defaultImageSet.data?.images;
  const groupedImages = groupImagesByCategory(images);
  const [activeCategory, setActiveCategory] = useState(Object.keys(groupedImages)?.at(0) ?? '');
  const categoryImages = groupedImages[activeCategory];
  const userRights = useSelector(getUserRights);
  const hasAdditionalRights = promotion.promotionType === 'DISCOUNT' && userRights.includes(UserRole.COUNTRY_ADMIN);

  useEffect(() => {
    const defaultCategory = Object.keys(groupedImages)?.at(0);
    if (defaultCategory) {
      setActiveCategory(defaultCategory);
    }
    // eslint-disable-next-line
  }, [defaultImageSet]);

  if (!activeCategory) {
    return null;
  }

  const SystemImage = ({ image }: SystemImageProps) => {
    const [showImage, setShowImage] = useState(false);
    if (!image) {
      return null;
    }
    const isSelected =
      image.imageKey === promotionImage?.key || image.imageKey === promotion.additionalInformation?.image?.key;

    const onManageImage = (action: 'ADD' | 'DELETE') => {
      const hasImage = promotion.image;
      const hasAdditionalImage = promotion.additionalInformation.image;
      const isPrimaryImage = image.imageKey === promotionImage?.key;
      const isAdditionalImage = image.imageKey === promotion.additionalInformation?.image?.key;

      const setAsPrimaryImage = () => {
        setPromotion({
          ...promotion,
          image: {
            key: image?.imageKey,
            name: image?.fileName,
            extension: 'png',
          },
        });
        if (!hasAdditionalRights) {
          setIsOpenImagesModal(false);
        }
      };
      const removePrimaryImage = () => {
        setPromotion({
          ...promotion,
          image: undefined,
        });
      };

      const setAsAdditionalImage = () => {
        setPromotion({
          ...promotion,
          additionalInformation: {
            ...promotion.additionalInformation,
            image: {
              key: image?.imageKey,
              name: image?.fileName,
              extension: 'png',
            },
          },
        });
      };

      const setSwitchAndRemoveImages = () => {
        setPromotion({
          ...promotion,
          image: {
            key: promotion.additionalInformation.image?.key,
            name: promotion.additionalInformation.image?.name,
            extension: 'png',
          },
          additionalInformation: {
            ...promotion.additionalInformation,
            image: undefined,
          },
        });
      };

      const removeAdditionalImage = () => {
        setPromotion({
          ...promotion,
          additionalInformation: { ...promotion.additionalInformation, image: undefined },
        });
      };
      switch (action) {
        case 'ADD':
          if (hasImage && hasAdditionalRights) {
            setAsAdditionalImage();
          } else {
            setAsPrimaryImage();
          }
          break;
        case 'DELETE':
          if (isAdditionalImage) {
            removeAdditionalImage();
            break;
          }
          if (isPrimaryImage && hasAdditionalRights && hasAdditionalImage) {
            setSwitchAndRemoveImages();
            break;
          }
          removePrimaryImage();
          break;
        default:
          break;
      }
    };

    const getImageNumber = () => {
      if (image.imageKey === promotionImage?.key) {
        return '1';
      }
      if (image.imageKey === promotion.additionalInformation?.image?.key) {
        return '2';
      }
      return '';
    };

    return (
      <ImageWrapper
        onMouseEnter={() => setShowImage(true)}
        onMouseLeave={() => setShowImage(false)}
        isSelected={isSelected}
      >
        <Image alt={'promotion_image'} type={'url'} src={image.imageUrl} width={'100%'} height={'100%'} />
        {(showImage || isSelected) && (
          <ImageButtonWrapper>
            <Flex direction={'column'} justify={'space-between'} minHeight={'100%'}>
              <Flex justify={'space-between'}>
                {isSelected && (
                  <>
                    {hasAdditionalRights ? (
                      <ImageNumberWrapper>
                        <CenterFlex>{getImageNumber()}</CenterFlex>
                      </ImageNumberWrapper>
                    ) : (
                      <Flex />
                    )}
                    <ImageDeleteWrapper onClick={() => onManageImage('DELETE')}>
                      <CenterFlex>
                        <Icon IconComponent={DeleteCrossIcon} size={24} />{' '}
                      </CenterFlex>
                    </ImageDeleteWrapper>
                  </>
                )}
              </Flex>
              <BlueButton stretch onClick={() => onManageImage('ADD')} disabled={isSelected}>
                <CenterFlex>{t('promotion.actions.use_this_picture', 'Use this picture')}</CenterFlex>
              </BlueButton>
            </Flex>
          </ImageButtonWrapper>
        )}
      </ImageWrapper>
    );
  };

  return (
    <Flex direction={'column'} gap={25} size={0}>
      <Flex gap={25}>
        {Object.keys(groupedImages).map((category) => (
          <ImageCategory
            key={category}
            isActive={activeCategory === category}
            onClick={() => setActiveCategory(category)}
          >
            <Text type="base" cursor={'pointer'} displayStyle={activeCategory === category ? 'info' : 'default'}>
              {getCategoryLabel(t, category.toUpperCase() as SystemImageType)}
            </Text>
          </ImageCategory>
        ))}
      </Flex>
      <Flex gap={15} wrap={'wrap'}>
        {categoryImages.map((image, index) => (
          <SystemImage image={image} key={`${activeCategory}-${index}`} />
        ))}
      </Flex>
      {hasAdditionalRights && (
        <Flex justify={'center'}>
          <Box>
            <BlackButton disabled={!promotion.image} onClick={() => setIsOpenImagesModal(false)}>
              Validate
            </BlackButton>
          </Box>
        </Flex>
      )}
    </Flex>
  );
}
